/**
 * Relatos: os problemas, sugestões e dúvidas enviados pelo botão "Relatar".
 * Cada um mostra quem mandou, de qual tela (com link para abrir a mesma tela),
 * a versão do sistema e o último erro visto. Quem relatou pode "Acrescentar
 * informação" (o relato reabre); o administrador responde e marca como
 * resolvido. Nada do que já foi escrito é apagado (o servidor acrescenta com
 * nome e data). "Copiar os abertos" junta tudo num texto para mandar ao suporte.
 *
 * Lê: api.relatos. Grava: api.responderRelato.
 * Usado por: App.tsx (rota #/relatos).
 */
import { useEffect, useState } from 'react';
import { Aviso, ErroNaTela } from '../componentes/basicos.tsx';
import { api, ErroApi, type Relato } from '../dados/api.ts';
import type { Usuario } from '../dominio/tipos.ts';
import { formatarDataHora } from '../formatacao.ts';

const NOME_DO_TIPO: Record<Relato['tipo'], string> = { problema: 'Problema', sugestao: 'Sugestão', duvida: 'Dúvida' };

/** O último erro guardado no relato, em texto ("API-409 (pedido 8ef1…) às 19:43: mensagem"). */
function descreverErro(relato: Relato): string {
  const erro = relato.contexto['ultimoErro'] as { codigo?: string; pedido?: string; mensagem?: string; quando?: string } | null | undefined;
  if (!erro?.codigo) return '';
  const quando = erro.quando ? ` às ${new Date(erro.quando).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : '';
  return `${erro.codigo}${erro.pedido ? ` (pedido ${erro.pedido})` : ''}${quando}: ${erro.mensagem ?? ''}`;
}

/** O relato em texto corrido (para colar no WhatsApp ou mandar ao suporte). */
function comoTexto(relato: Relato): string {
  const erro = descreverErro(relato);
  return [
    `#${relato.id} · ${NOME_DO_TIPO[relato.tipo]} · ${formatarDataHora(relato.criadoEm)} · ${relato.criadoPor}`,
    relato.texto,
    `Tela: ${relato.tela || '—'} · versão ${String(relato.contexto['versao'] ?? '?')} · ${String(relato.contexto['tela'] ?? '')}`,
    erro ? `Último erro: ${erro}` : '',
    relato.resposta ? `Resposta: ${relato.resposta}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

export function Relatos({ usuario }: { usuario: Usuario }) {
  const [relatos, setRelatos] = useState<Relato[] | null>(null);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const [soAbertos, setSoAbertos] = useState(true);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    api
      .relatos()
      .then(({ relatos }) => setRelatos(relatos))
      .catch((falha: unknown) => setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null)));
  }, []);

  const lista = (relatos ?? []).filter((r) => !soAbertos || r.situacao === 'aberto');
  const abertos = (relatos ?? []).filter((r) => r.situacao === 'aberto');

  async function copiar() {
    try {
      await navigator.clipboard.writeText(abertos.map(comoTexto).join('\n\n'));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 3000);
    } catch {
      setErro(new ErroApi('APP', 'O navegador não deixou copiar. Selecione o texto dos relatos e copie com Ctrl+C.', 0, null));
    }
  }

  return (
    <div className="pagina">
      <div className="pagina-titulo">
        <div>
          <h1>Relatos</h1>
          <p>O que foi encontrado nos testes: problemas, sugestões e dúvidas. Para mandar um novo, use o botão "Relatar" no topo de qualquer tela.</p>
        </div>
        <div className="acoes">
          <div className="segmentado" role="group" aria-label="Quais relatos">
            <button type="button" aria-pressed={soAbertos} onClick={() => setSoAbertos(true)}>
              Abertos ({abertos.length})
            </button>
            <button type="button" aria-pressed={!soAbertos} onClick={() => setSoAbertos(false)}>
              Todos ({relatos?.length ?? 0})
            </button>
          </div>
          <button type="button" className="botao-secundario" onClick={() => void copiar()} disabled={abertos.length === 0}>
            {copiado ? 'Copiado ✓' : 'Copiar os abertos'}
          </button>
        </div>
      </div>
      {erro ? <ErroNaTela erro={erro} /> : null}
      {relatos === null && !erro ? <p className="texto-apagado">Carregando…</p> : null}
      {relatos !== null && lista.length === 0 ? <Aviso tipo="ok">{soAbertos ? 'Nenhum relato aberto.' : 'Nenhum relato ainda.'}</Aviso> : null}
      {lista.map((relato) => (
        <CartaoDoRelato key={relato.id} relato={relato} admin={usuario.papel === 'admin'} aoMudar={(novo) => setRelatos((atual) => (atual ?? []).map((r) => (r.id === novo.id ? novo : r)))} />
      ))}
    </div>
  );
}

function CartaoDoRelato({ relato, admin, aoMudar }: { relato: Relato; admin: boolean; aoMudar: (relato: Relato) => void }) {
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<ErroApi | null>(null);
  const erroVisto = descreverErro(relato);

  async function enviar(acao: () => Promise<{ relato: Relato }>) {
    setSalvando(true);
    setErro(null);
    try {
      aoMudar((await acao()).relato);
      setTexto('');
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha : new ErroApi('APP', String(falha), 0, null));
    } finally {
      setSalvando(false);
    }
  }

  return (
    <section className="cartao pilha">
      <div className="pagina-titulo">
        <div>
          <h2>
            <span className={relato.tipo === 'problema' ? 'etiqueta etiqueta-alerta' : 'etiqueta'}>{NOME_DO_TIPO[relato.tipo]}</span> #{relato.id}
          </h2>
          <p className="subtitulo">
            {relato.criadoPor} · {formatarDataHora(relato.criadoEm)} · {relato.tela.startsWith('#/') ? <a href={relato.tela}>abrir a tela</a> : 'sem tela'}
          </p>
        </div>
        <span className={relato.situacao === 'resolvido' ? 'etiqueta etiqueta-bom' : 'etiqueta'}>{relato.situacao === 'resolvido' ? '✓ Resolvido' : 'Aberto'}</span>
      </div>
      <p className="texto-do-relato">{relato.texto}</p>
      <details>
        <summary>Detalhes para o suporte</summary>
        <p className="texto-pequeno texto-apagado">
          Tela: {relato.tela || '—'} · versão {String(relato.contexto['versao'] ?? '?')} · {String(relato.contexto['tela'] ?? '')}
          {erroVisto ? ` · último erro: ${erroVisto}` : ' · nenhum erro nos 15 minutos antes do relato'}
        </p>
      </details>
      {relato.resposta ? (
        <Aviso tipo={relato.situacao === 'resolvido' ? 'ok' : 'info'}>
          <span className="texto-do-relato">{relato.resposta}</span>
        </Aviso>
      ) : null}
      <label>
        {admin ? 'Responder' : 'Acrescentar informação'}
        <textarea rows={2} value={texto} onChange={(e) => setTexto(e.target.value)} maxLength={4000} />
      </label>
      {erro ? <ErroNaTela erro={erro} /> : null}
      <div className="linha-campos">
        {admin ? (
          <>
            {relato.situacao === 'aberto' ? (
              <button type="button" className="botao" onClick={() => void enviar(() => api.responderRelato(relato.id, 'resolvido', texto))} disabled={salvando}>
                {texto.trim() ? 'Responder e marcar como resolvido' : 'Marcar como resolvido'}
              </button>
            ) : (
              <button type="button" className="botao-secundario" onClick={() => void enviar(() => api.responderRelato(relato.id, 'aberto', texto))} disabled={salvando}>
                Reabrir
              </button>
            )}
            <button type="button" className="botao-secundario" onClick={() => void enviar(() => api.responderRelato(relato.id, relato.situacao, texto))} disabled={salvando || texto.trim() === ''}>
              Só responder
            </button>
          </>
        ) : (
          <button type="button" className="botao-secundario" onClick={() => void enviar(() => api.complementarRelato(relato.id, texto))} disabled={salvando || texto.trim() === ''}>
            Acrescentar
          </button>
        )}
      </div>
    </section>
  );
}
